/**
 * resourceSchedule.js — read "what is this person on now, and what next" off
 * the Manpower calendar.
 *
 * The calendar stores one label per engineer per day. People think in
 * assignments, not days, so consecutive days carrying the same label are
 * collapsed into a run with a start and an end. The Resources list then shows
 * the run covering today, and the next different run after it.
 *
 * Nothing is typed twice: the board IS the source, so the list cannot drift
 * from it.
 *
 * Pure — pass `today` rather than reading a clock. Tested in
 * src/utils/__tests__/resourceSchedule.test.js.
 */
import { isTimeOff } from './utilizationFromCalendar';
import { localDay as ymd } from './localDay';

const parse = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** One day after `iso`, as YYYY-MM-DD. */
const nextDay = (iso) => {
  const d = parse(iso);
  d.setDate(d.getDate() + 1);
  return ymd(d);
};

/**
 * Every assignment run for one engineer, oldest first.
 * A gap of any length ends a run; so does a change of label.
 *
 * @param name   engineer name, the key the calendar uses
 * @param edits  { 'Name|YYYY-MM-DD': 'Label' | null }
 * @returns [{ project, start, end, days }]
 */
export function runsFor(name, edits) {
  const prefix = `${name}|`;
  const days = Object.keys(edits || {})
    .filter((k) => k.startsWith(prefix) && edits[k])
    .map((k) => ({ day: k.slice(prefix.length), label: edits[k] }))
    .sort((a, b) => a.day.localeCompare(b.day));

  const runs = [];
  for (const { day, label } of days) {
    const last = runs[runs.length - 1];
    // Same label AND contiguous (weekends included — a Mon-Fri block that
    // resumes the next Monday is one assignment to a human, so bridge the gap
    // when nothing else was scheduled in between).
    if (last && last.project === label && bridges(last.end, day)) {
      last.end = day;
      last.days += 1;
    } else {
      runs.push({ project: label, start: day, end: day, days: 1 });
    }
  }
  return runs;
}

/** True when `to` follows `from` with only weekend days in between. */
function bridges(from, to) {
  let cursor = nextDay(from);
  let hops = 0;
  while (cursor < to && hops < 4) {
    const dow = parse(cursor).getDay();
    if (dow !== 0 && dow !== 6) return false; // a working day was skipped
    cursor = nextDay(cursor);
    hops += 1;
  }
  return cursor === to;
}

/**
 * What this person is on now and what comes next.
 * Time off (PTO / vacation / holiday) is skipped when picking the NEXT
 * assignment — "future project" means the next piece of work, not the next
 * time they are away.
 *
 * @returns { current: run|null, next: run|null, runs }
 */
export function resourceSchedule(name, edits, today = new Date()) {
  const runs = runsFor(name, edits);
  const t = ymd(today);

  const current = runs.find((r) => r.start <= t && t <= r.end) || null;
  const next = runs.find((r) => r.start > t && !isTimeOff(r.project)) || null;

  return { current, next, runs };
}
